
/**
 * Module dependencies.
 */

import { Menu, Name as MenuName } from 'src/core/menu';
import { borderOpacity } from './nav-theme';
import { color, media, units } from 'src/styles/utils';
import { ifProp, theme } from 'styled-tools';
import { isInternalRoute } from './nav-element';
import { useLanguageLinks } from './use-language-links';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useTranslate } from 'src/core/utils/translator';
import NavLink from './nav-link';
import NavTertiaryLink from './nav-tertiary-link';
import NavbarButton from './navbar-button';
import React, {
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react';
import RouterLink from 'src/components/core/links/router-link';
import Svg from 'src/components/core/svg';
import arrowLeftIcon from 'src/assets/svg/arrow-left-2.svg';
import caretIcon from 'src/assets/svg/caret.svg';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useTopBarMenu from 'src/api/app/top-bar-menu/use-top-bar-menu';

/**
 * Slide timings.
 */

const slideDuration = '0.35s';
const slideTransition = `${slideDuration} cubic-bezier(0.4, 0, 0.2, 1)`;

/**
 * `Props` type.
 */

type Props = {
  isOpen: boolean,
  menu: Menu[],
  onClose: () => void
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<{ isOpen: boolean }>`
  background-color: ${color('white')};
  bottom: 0;
  display: flex;
  flex-direction: column;
  left: 0;
  overflow-x: hidden;
  overflow-y: auto;
  position: fixed;
  right: 0;
  top: ${theme('dimensions.navbarHeightMobile')}px;
  transition: ${theme('animations.fastTransition')};
  transition-property: opacity, visibility;

  ${ifProp('isOpen', css`
    opacity: 1;
    visibility: visible;
  `, css`
    opacity: 0;
    visibility: hidden;
  `)}

  ${media.min('md')`
    display: none;
  `}
`;

/**
 * `Content` styled component.
 */

const Content = styled.div`
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  justify-content: space-between;
`;

/**
 * `Viewport` styled component.
 */

const Viewport = styled.div`
  overflow: hidden;
`;

/**
 * `Track` styled component.
 *
 * Holds both levels side by side and slides between them. The height follows
 * whichever level is showing, so the actions below never jump.
 */

const Track = styled.div<{ hasSubMenu: boolean }>`
  align-items: start;
  display: grid;
  grid-template-columns: 100% 100%;
  transition: ${slideTransition};
  transition-property: height, transform;

  ${ifProp('hasSubMenu', css`
    transform: translateX(-100%);
  `)}
`;

/**
 * `Panel` styled component.
 *
 * The active panel inherits its visibility rather than forcing `visible`: with
 * an explicit value it would override the `Wrapper`, leaving the links of the
 * closed drawer in the tab order even though it is `aria-hidden`.
 */

const Panel = styled.div<{ isActive: boolean }>`
  min-width: 0;
  transition: visibility 0s linear ${slideDuration};

  ${ifProp('isActive', css`
    transition-delay: 0s;
    visibility: inherit;
  `, css`
    visibility: hidden;
  `)}
`;

/**
 * `Section` styled component.
 */

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(3)};
  padding: ${units(2.5)} ${units(2.5)} 0;
`;

/**
 * `MenuItem` styled component.
 */

const MenuItem = styled.button`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  color: ${color('black')};
  cursor: pointer;
  display: flex;
  font-size: 28px;
  gap: ${units(2)};
  letter-spacing: 0.01em;
  line-height: 1.3;
  outline: none;
  padding: ${units(1.5)} 0;
  text-align: left;
  text-transform: uppercase;
  width: 100%;
`;

/**
 * `MenuItemLabel` styled component.
 */

const MenuItemLabel = styled.span`
  flex: 1 0 0;
  min-width: 0;
`;

/**
 * `Chevron` styled component.
 */

const Chevron = styled.span`
  flex: none;
  line-height: 0;
  transform: rotate(-90deg);
`;

/**
 * `TertiaryLinks` styled component.
 */

const TertiaryLinks = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;

  > * {
    padding-left: 0;
    padding-right: 0;
  }
`;

/**
 * `BackButton` styled component.
 */

const BackButton = styled.button`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  background: none;
  border: 0;
  color: ${color('brown200')};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  gap: ${units(1)};
  letter-spacing: 0.01em;
  line-height: 1.5;
  outline: none;
  padding: 0;
  text-decoration: underline;
  text-transform: uppercase;
`;

/**
 * `SubMenuTitle` styled component.
 */

const SubMenuTitle = styled.p`
  border-bottom: 1px solid ${color.transparentize('neutralDarkest', borderOpacity)};
  color: ${color('black')};
  font-size: 28px;
  letter-spacing: 0.01em;
  line-height: 1.3;
  margin: 0;
  padding: ${units(1.5)} 0;
  text-transform: uppercase;
`;

/**
 * `Groups` styled component.
 */

const Groups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(4)};
  padding: ${units(2)} 0 ${units(5)} ${units(1)};
`;

/**
 * `Group` styled component.
 */

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(1.5)};
`;

/**
 * `GroupTitle` styled component.
 */

const GroupTitle = styled.p`
  color: ${color('black')};
  font-size: 16px;
  font-weight: 600;
  line-height: ${units(3)};
  margin: 0;
`;

/**
 * `List` styled component.
 */

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${units(1.5)};
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  background-color: ${color('beige100')};
  display: flex;
  flex-direction: column;
  gap: ${units(2)};
  margin-top: ${units(4)};
  padding: ${units(4)} ${units(2.5)} ${units(10)};

  > button,
  > a {
    width: 100%;
  }
`;

/**
 * `Languages` styled component.
 */

const Languages = styled.div`
  display: flex;
  gap: ${units(1)};
  justify-content: center;
`;

/**
 * `LanguageLink` styled component.
 */

const LanguageLink = styled(RouterLink)<{ isActive: boolean }>`
  color: ${color('black')};
  font-size: 12px;
  letter-spacing: 0.01em;
  line-height: 1.5;
  text-transform: uppercase;

  ${ifProp('isActive', css`
    font-weight: 600;
  `)}
`;

/**
 * `MobileMenu` component.
 */

const MobileMenu = ({ isOpen, menu, onClose }: Props): ReactElement => {
  const { locale, translate } = useTranslate();
  const routeResolver = useLocalizedRoute();
  const languageLinks = useLanguageLinks();
  const { data: topBarData } = useTopBarMenu();
  const topBarLinks = isArray(topBarData) ? topBarData : [];
  const [activeMenu, setActiveMenu] = useState<MenuName | null>(null);
  const [displayedName, setDisplayedName] = useState<MenuName | null>(null);
  const [trackHeight, setTrackHeight] = useState<number>();
  const rootRef = useRef<HTMLDivElement>(null);
  const subMenuRef = useRef<HTMLDivElement>(null);
  const menuRefs = useRef<Partial<Record<MenuName, HTMLButtonElement>>>({});
  const hasSubMenu = !!find(menu, { name: activeMenu });
  const displayedEntry = find(menu, { name: displayedName });

  // Keeps the sub menu mounted while it slides back out.
  useEffect(() => {
    if (activeMenu) {
      setDisplayedName(activeMenu);
    }
  }, [activeMenu]);

  // Escape steps back out of the sub menu, then closes the drawer.
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = ({ key }: KeyboardEvent) => {
      if (key !== 'Escape') {
        return;
      }

      if (activeMenu) {
        setActiveMenu(null);

        return;
      }

      onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeMenu, isOpen, onClose]);

  // Going back to the root level returns focus to the entry that was opened.
  useEffect(() => {
    if (!activeMenu && displayedName) {
      menuRefs.current[displayedName]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMenu]);

  // The track has to follow the height of whichever level is showing.
  useEffect(() => {
    const element = hasSubMenu ? subMenuRef.current : rootRef.current;

    if (!element) {
      return;
    }

    const updateHeight = () => setTrackHeight(element.offsetHeight);

    updateHeight();

    const observer = new ResizeObserver(updateHeight);

    observer.observe(element);

    return () => observer.disconnect();
  }, [displayedName, hasSubMenu, isOpen, menu, topBarLinks.length]);

  return (
    <Wrapper
      aria-hidden={!isOpen}
      isOpen={isOpen}
    >
      <Content>
        <Viewport>
          <Track
            hasSubMenu={hasSubMenu}
            style={{ height: trackHeight }}
          >
            <Panel
              isActive={!hasSubMenu}
              ref={rootRef}
            >
              <Section>
                <div>
                  {map(menu, ({ name, title }) => (
                    <MenuItem
                      aria-expanded={activeMenu === name}
                      key={name}
                      onClick={() => setActiveMenu(name)}
                      ref={(element: HTMLButtonElement) => {
                        menuRefs.current[name] = element;
                      }}
                      type={'button'}
                    >
                      <MenuItemLabel>
                        {title}
                      </MenuItemLabel>

                      <Chevron>
                        <Svg
                          icon={caretIcon}
                          size={units(3)}
                        />
                      </Chevron>
                    </MenuItem>
                  ))}
                </div>

                <TertiaryLinks>
                  {map(topBarLinks, ({ label, link }) => link && (
                    <NavTertiaryLink
                      colorTheme={'dark'}
                      href={link}
                      key={label}
                      onClick={onClose}
                      {...isInternalRoute(link) && { locale }}
                    >
                      {label}
                    </NavTertiaryLink>
                  ))}
                </TertiaryLinks>
              </Section>
            </Panel>

            <Panel
              isActive={hasSubMenu}
              ref={subMenuRef}
            >
              {displayedEntry && (
                <Section>
            <BackButton
              onClick={() => setActiveMenu(null)}
              type={'button'}
            >
              <Svg
                icon={arrowLeftIcon}
                size={units(3)}
              />

              {translate('common:actions.back')}
            </BackButton>

            <div>
              <SubMenuTitle>
                {displayedEntry.title}
              </SubMenuTitle>

              <Groups>
                {map(displayedEntry.groups, ({ links, title }) => (
                  <Group key={title}>
                    <GroupTitle>
                      {title}
                    </GroupTitle>

                    <List>
                      {map(links, ({ href, label }) => (
                        <li key={label}>
                          <NavLink
                            href={href}
                            onClick={onClose}
                            {...isInternalRoute(href) && { locale }}
                          >
                            {label}
                          </NavLink>
                        </li>
                      ))}
                    </List>
                  </Group>
                ))}
              </Groups>
            </div>
                </Section>
              )}
            </Panel>
          </Track>
        </Viewport>

        <Actions>
          <NavbarButton
            colorTheme={'dark'}
            href={routeResolver('recommend')}
            locale={locale}
            onClick={onClose}
            variant={'outlined'}
          >
            {translate('menu:toolsMenu.recommender')}
          </NavbarButton>

          <NavbarButton
            colorTheme={'dark'}
            href={routeResolver('whereToBuy')}
            locale={locale}
            onClick={onClose}
            variant={'fill'}
          >
            {translate('menu:whereToBuy')}
          </NavbarButton>

          <Languages>
            {map(languageLinks, ({ href, isActive, locale: language }) => (
              <LanguageLink
                href={href}
                isActive={isActive}
                key={language}
                locale={language}
              >
                {language}
              </LanguageLink>
            ))}
          </Languages>
        </Actions>
      </Content>
    </Wrapper>
  );
};

/**
 * Export `MobileMenu` component.
 */

export default MobileMenu;
